import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, orderBy, getDoc, serverTimestamp } from 'firebase/firestore';
import { 
  FaRobot, FaPlus, FaTrash, FaBolt, FaTimes, FaCommentDots, 
  FaToggleOn, FaToggleOff, FaMagic, FaRegLightbulb, FaShieldAlt, 
  FaPen, FaMicrochip, FaBrain, FaCogs, FaCheckCircle, FaNetworkWired, FaStar
} from 'react-icons/fa';

// --- ULTRA PREMIUM THEME ---
const THEME = {
  primary: '#7c3aed', // Violet
  primaryGradient: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
  accent: '#2dd4bf',  // Teal
  accentGradient: 'linear-gradient(135deg, #2dd4bf 0%, #0d9488 100%)',
  bg: '#f8fafc',
  card: '#ffffff',
  textMain: '#0f172a',
  textSub: '#64748b',
  border: '#e2e8f0',
  success: '#10b981',
  error: '#ef4444',
  darkBg: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)' // Deep Space Dark
};

// --- CUSTOM CSS INJECTIONS ---
const injectStyles = () => {
  if (document.getElementById('bot-styles')) return;
  const style = document.createElement('style');
  style.id = 'bot-styles';
  style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
    
    * { box-sizing: border-box; }

    .bot-container { font-family: 'Plus Jakarta Sans', sans-serif; }
    
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes pulseGlow {
      0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
      70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
      100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
    }

    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }
    
    .animate-up { animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
    .delay-1 { animation-delay: 0.1s; }
    
    .rule-card {
      background: white;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.8);
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.02);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .rule-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px -10px rgba(124, 58, 237, 0.15);
      border-color: #d8b4fe;
    }

    .glass-input {
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.1);
      color: white;
      transition: all 0.3s ease;
    }
    .glass-input:focus {
      background: rgba(255,255,255,0.08);
      border-color: ${THEME.accent};
      box-shadow: 0 0 0 4px rgba(45, 212, 191, 0.1);
    }
    
    .custom-scroll::-webkit-scrollbar { width: 6px; }
    .custom-scroll::-webkit-scrollbar-track { background: transparent; }
    .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

    /* Custom Toggle Switch for AI */
    .toggle-checkbox:checked { right: 0; border-color: ${THEME.success}; }
    .toggle-checkbox:checked + .toggle-label { background-color: ${THEME.success}; }
    .toggle-checkbox:checked + .toggle-label:after { transform: translateX(100%); border-color: white; }
    .toggle-label {
      width: 50px; height: 26px;
      background-color: rgba(255,255,255,0.2);
      border-radius: 9999px; position: relative; cursor: pointer; transition: background-color 0.3s;
    }
    .toggle-label:after {
      content: ''; position: absolute; top: 2px; left: 2px; width: 22px; height: 22px;
      background-color: white; border-radius: 50%; transition: transform 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
  `;
  document.head.appendChild(style);
};

function BotManager() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('RULES'); 
  const [showModal, setShowModal] = useState(false);
  
  // Data
  const [botActive, setBotActive] = useState(false);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // AI Brain State (SaaS Multi-tenant ready - ONLY System Prompt needed)
  const [aiSettings, setAiSettings] = useState({
    isActive: false, systemPrompt: ''
  });

  // Form State
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [triggerKeyword, setTriggerKeyword] = useState("");
  const [matchType, setMatchType] = useState("contains"); 
  const [actionType, setActionType] = useState("text"); 
  const [replyText, setReplyText] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    injectStyles();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        // Load Bot Master Settings
        const botSettingsRef = doc(db, "businesses", u.uid, "settings", "bot");
        const botSnap = await getDoc(botSettingsRef);
        if (botSnap.exists()) setBotActive(botSnap.data().isActive || false);

        // Load AI Settings specifically for this business
        const aiSettingsRef = doc(db, "businesses", u.uid, "settings", "ai_brain");
        const aiSnap = await getDoc(aiSettingsRef);
        if (aiSnap.exists()) {
            setAiSettings({
                isActive: aiSnap.data().isActive || false,
                systemPrompt: aiSnap.data().systemPrompt || ''
            });
        }

        // Load Rules
        const qRules = query(collection(db, "businesses", u.uid, "bot_rules"), orderBy("createdAt", "desc"));
        onSnapshot(qRules, (snapshot) => {
          setRules(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Load Templates
        const qTemplates = query(collection(db, "businesses", u.uid, "templates"));
        onSnapshot(qTemplates, (snapshot) => {
          setTemplates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const toggleBotStatus = async () => {
    if (!user) return;
    const newStatus = !botActive;
    setBotActive(newStatus);
    await setDoc(doc(db, "businesses", user.uid, "settings", "bot"), { isActive: newStatus }, { merge: true });
  };

  const handleSaveAiSettings = async () => {
    if (aiSettings.isActive && !aiSettings.systemPrompt.trim()) return alert("A System Prompt is required to activate the AI. Tell the AI how to behave!");
    
    setIsSaving(true);
    // Saves ONLY the prompt and status for this specific business
    await setDoc(doc(db, "businesses", user.uid, "settings", "ai_brain"), aiSettings, { merge: true });
    alert("AI Brain settings saved securely.");
    setIsSaving(false);
  };

  // --- FORM HANDLERS ---
  const handleOpenNewModal = () => {
    setEditingRuleId(null); setTriggerKeyword(""); setMatchType("contains"); setActionType("text"); setReplyText(""); setSelectedTemplate(""); setShowModal(true);
  };

  const handleEditRule = (rule) => {
    setEditingRuleId(rule.id); setTriggerKeyword(rule.trigger); setMatchType(rule.matchType || "contains"); setActionType(rule.actionType || "text");
    if (rule.actionType === 'template') { setSelectedTemplate(rule.response); setReplyText(""); } 
    else { setReplyText(rule.response); setSelectedTemplate(""); }
    setShowModal(true);
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (!user || !triggerKeyword) return;
    if (actionType === 'text' && !replyText) return alert("Please enter a reply text.");
    if (actionType === 'template' && !selectedTemplate) return alert("Please select a template.");

    setIsSaving(true);
    try {
      const ruleId = editingRuleId ? editingRuleId : (triggerKeyword.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now());
      
      const payload = {
        trigger: triggerKeyword.toLowerCase(), matchType: matchType, actionType: actionType, 
        response: actionType === 'text' ? replyText : selectedTemplate, updatedAt: serverTimestamp()
      };
      if (!editingRuleId) payload.createdAt = serverTimestamp();

      await setDoc(doc(db, "businesses", user.uid, "bot_rules", ruleId), payload, { merge: true });
      setShowModal(false); setEditingRuleId(null); setTriggerKeyword(""); setReplyText(""); setSelectedTemplate("");
    } catch (error) { console.error(error); alert("Failed to save rule."); }
    setIsSaving(false);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!user || !window.confirm("Delete this automation rule?")) return;
    await deleteDoc(doc(db, "businesses", user.uid, "bot_rules", ruleId));
  };

  if (!user) return <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:THEME.textSub, fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '18px', fontWeight: '800' }}><div style={{width:'24px', height:'24px', border:'3px solid #e2e8f0', borderTopColor: THEME.primary, borderRadius:'50%', animation:'spin 1s linear infinite', marginRight:'10px'}}></div> Initializing AI Systems...</div>;

  return (
    <div className="bot-container" style={{ background: THEME.bg, minHeight: '100vh', padding: isMobile ? '20px 15px' : '40px 50px', overflowX: 'hidden' }}>
      
      {/* HEADER */}
      <div className="animate-up" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: isMobile ? '25px' : '40px', gap: '20px' }}>
        <div style={{ width: isMobile ? '100%' : 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: '900', color: THEME.textMain, margin: 0, letterSpacing: '-1px' }}>AI Flow Builder</h1>
            {botActive && (
              <span style={{ background: '#dcfce7', color: '#047857', fontSize: '10px', fontWeight: '900', padding: '6px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #10b981', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.15)' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', animation: 'pulseGlow 2s infinite' }}></div> ONLINE
              </span>
            )}
          </div>
          <p style={{ margin: '8px 0 0 0', color: THEME.textSub, fontSize: '14px', fontWeight: '600' }}>Automate customer conversations with smart logic or True AI.</p>
        </div>

        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
          {/* MASTER SWITCH */}
          <div onClick={toggleBotStatus} style={{ flex: isMobile ? 1 : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px', background: botActive ? THEME.primaryGradient : 'white', padding: '12px 20px', borderRadius: '16px', border: botActive ? 'none' : `2px solid ${THEME.border}`, boxShadow: botActive ? '0 10px 25px -5px rgba(124, 58, 237, 0.4)' : '0 4px 6px rgba(0,0,0,0.02)', transition: 'all 0.3s ease' }}>
            <span style={{ fontSize: '14px', fontWeight: '800', color: botActive ? 'white' : THEME.textSub }}>{botActive ? 'Auto-Reply ON' : 'Auto-Reply OFF'}</span>
            {botActive ? <FaToggleOn size={32} color="white" /> : <FaToggleOff size={32} color={THEME.textSub} />}
          </div>
        </div>
      </div>

      {/* ULTRA TAB NAVIGATION (Responsive Stacking) */}
      <div className="animate-up delay-1" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginBottom: '35px', background: 'white', padding: '10px', borderRadius: '20px', width: isMobile ? '100%' : 'auto', boxShadow: '0 4px 20px -5px rgba(0,0,0,0.05)', border: `1px solid ${THEME.border}`, overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <button onClick={() => setActiveTab('RULES')} style={{ flex: isMobile ? 1 : 'none', justifyContent: 'center', padding: '14px 28px', borderRadius: '14px', border: 'none', background: activeTab === 'RULES' ? THEME.bg : 'transparent', color: activeTab === 'RULES' ? THEME.primary : THEME.textSub, fontWeight: '800', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
          <FaCogs size={18} /> Keyword Rules
        </button>
        <button onClick={() => setActiveTab('AI_BRAIN')} style={{ flex: isMobile ? 1 : 'none', justifyContent: 'center', padding: '14px 28px', borderRadius: '14px', border: 'none', background: activeTab === 'AI_BRAIN' ? THEME.bg : 'transparent', color: activeTab === 'AI_BRAIN' ? THEME.primary : THEME.textSub, fontWeight: '800', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
          <FaMicrochip size={18} /> True AI Brain
        </button>
      </div>

      {/* =========================================
          TAB 1: KEYWORD RULES (GLASSMORPHISM)
      ========================================= */}
      {activeTab === 'RULES' && (
        <div style={{ animation: 'fadeUp 0.4s ease-out forwards' }}>
          
          <div style={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', marginBottom: '25px', width: '100%' }}>
            <button onClick={handleOpenNewModal} style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center', background: THEME.primaryGradient, color: 'white', border: 'none', padding: '16px 32px', borderRadius: '16px', fontWeight: '900', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.4)', transition: 'transform 0.2s', ':hover':{transform:'translateY(-2px)'} }}>
              <FaPlus /> Build New Flow
            </button>
          </div>

          <div className="rule-card" style={{ padding: isMobile ? '20px' : '30px', marginBottom: '40px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '20px', background: 'linear-gradient(to right, #ffffff, #f8fafc)' }}>
            <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', fontSize: '28px', flexShrink: 0 }}>
              <FaShieldAlt />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '900', color: THEME.textMain }}>Default Fallback Message</h3>
              <p style={{ margin: 0, fontSize: '14px', color: THEME.textSub, fontWeight: '500', lineHeight: '1.5' }}>This message is sent when the bot doesn't understand the user's query. It acts as a safety net to ensure no customer is ignored.</p>
            </div>
            <button style={{ padding: '14px 28px', borderRadius: '14px', border: `2px solid ${THEME.border}`, background: 'white', color: THEME.textMain, fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', width: isMobile ? '100%' : 'auto' }}>Configure Fallback</button>
          </div>

          {rules.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '32px', border: `2px dashed ${THEME.border}` }}>
              <div style={{width:'80px', height:'80px', borderRadius:'24px', background: THEME.bg, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px'}}>
                <FaRegLightbulb size={40} color={THEME.textSub} />
              </div>
              <h3 style={{ margin: '0 0 10px 0', color: THEME.textMain, fontSize: '24px', fontWeight: '900' }}>No Automation Flows Yet</h3>
              <p style={{ margin: 0, color: THEME.textSub, fontSize: '15px', fontWeight: '500' }}>Create your first rule to start automating responses.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(380px, 1fr))', gap: '25px' }}>
              {rules.map(rule => (
                <div key={rule.id} className="rule-card" style={{ padding: isMobile ? '20px' : '30px', display: 'flex', flexDirection: 'column' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '25px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: rule.matchType === 'exact' ? '#ede9fe' : '#ecfdf5', color: rule.matchType === 'exact' ? THEME.primary : THEME.success, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)', flexShrink: 0 }}>
                        {rule.matchType === 'exact' ? <FaBolt size={20} /> : <FaMagic size={20} />}
                      </div>
                      <div style={{ overflow: 'hidden' }}>
                        <span style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: THEME.textSub, letterSpacing: '0.5px' }}>{rule.matchType === 'exact' ? 'Exact Match' : 'Smart Contains'}</span>
                        <h3 style={{ margin: '4px 0 0 0', fontSize: '18px', fontWeight: '900', color: THEME.textMain, letterSpacing: '-0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>"{rule.trigger}"</h3>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '25px' }}>
                    <div style={{ flex: 1, height: '2px', background: THEME.bg, borderRadius: '2px' }}></div>
                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#94a3b8', background: THEME.bg, padding: '4px 12px', borderRadius: '12px', letterSpacing: '1px' }}>THEN EXECUTE</div>
                    <div style={{ flex: 1, height: '2px', background: THEME.bg, borderRadius: '2px' }}></div>
                  </div>

                  <div style={{ background: THEME.bg, padding: '20px', borderRadius: '16px', border: `1px solid ${THEME.border}`, flex: 1, marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <div style={{background: 'white', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'}}>
                        <FaCommentDots size={14} color={THEME.primary} />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: THEME.primary, letterSpacing: '0.5px' }}>
                        {rule.actionType === 'text' ? 'Send Text Reply' : 'Fire Template'}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: THEME.textMain, lineHeight: '1.6', fontWeight: '500', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {rule.actionType === 'text' ? rule.response : `Template Linked: ${templates.find(t => t.id === rule.response)?.name || rule.response}`}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
                    <button onClick={() => handleEditRule(rule)} style={{ flex: 1, border: `2px solid ${THEME.border}`, background: 'white', color: THEME.textMain, padding: '12px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '13px', fontWeight: '800', transition: 'all 0.2s' }}>
                      <FaPen color={THEME.primary}/> Edit Flow
                    </button>
                    <button onClick={() => handleDeleteRule(rule.id)} style={{ border: 'none', background: THEME.errorLight || '#fee2e2', color: THEME.error, padding: '12px 16px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s' }}>
                      <FaTrash size={16} />
                    </button>
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =========================================
          TAB 2: AI BRAIN (SaaS MASTER PROMPT VIEW)
      ========================================= */}
      {activeTab === 'AI_BRAIN' && (
        <div style={{ animation: 'fadeUp 0.4s ease-out forwards' }}>
          
          <div style={{ background: THEME.darkBg, borderRadius: '32px', padding: isMobile ? '25px 20px' : '50px', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.5), inset 0 0 0 1px rgba(255,255,255,0.1)' }}>
            
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '35px', gap: '20px', position: 'relative', zIndex: 2 }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '15px' }}>
                  <FaStar color="#fbbf24" /> Managed AI Infrastructure
                </div>
                <h2 style={{ margin: 0, fontSize: isMobile ? '26px' : '38px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '15px', letterSpacing: '-1px' }}>
                  Generative AI Brain
                </h2>
                <p style={{ margin: '10px 0 0 0', color: '#94a3b8', fontSize: '15px', fontWeight: '500', maxWidth: '600px', lineHeight: '1.6' }}>
                  Powered securely by Google Gemini. Just tell the AI about your business, your menu, and your prices, and it will handle the rest automatically.
                </p>
              </div>
              
              {/* Custom Toggle Switch for AI Engine Status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', background: 'rgba(255,255,255,0.05)', padding: '12px 20px', borderRadius: '16px', border: `1px solid rgba(255,255,255,0.1)`, backdropFilter: 'blur(10px)', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start', flexShrink: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', fontWeight: '900', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>AI Engine</span>
                  <span style={{ fontSize: '14px', fontWeight: '800', color: aiSettings.isActive ? THEME.success : 'white' }}>
                    {aiSettings.isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>
                <div style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
                  <input
                    type="checkbox"
                    checked={aiSettings.isActive}
                    onChange={(e) => setAiSettings({...aiSettings, isActive: e.target.checked})}
                    className="toggle-checkbox"
                    style={{ display: 'none' }}
                    id="ai-toggle"
                  />
                  <label htmlFor="ai-toggle" className="toggle-label"></label>
                </div>
              </div>
            </div>

            <div style={{ position: 'relative', zIndex: 2, background: 'rgba(0,0,0,0.2)', padding: isMobile ? '20px' : '30px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', color: THEME.accent, marginBottom: '15px', letterSpacing: '1px' }}>
                <FaBrain size={16} /> Business Identity & System Prompt
              </label>
              
              <textarea 
                value={aiSettings.systemPrompt} 
                onChange={e => setAiSettings({...aiSettings, systemPrompt: e.target.value})} 
                placeholder="Example: You are the friendly receptionist for [Your Business Name]. You speak politely. Here are our services: 1. Deep Tissue Massage ($50). 2. Swedish Massage ($40). Always ask the customer what time they want to book..." 
                className="glass-input custom-scroll" 
                style={{ width: '100%', minHeight: isMobile ? '250px' : '300px', padding: '20px', borderRadius: '16px', fontSize: '15px', fontFamily: '"Inter", sans-serif', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: '1.6' }} 
              />
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', position: 'relative', zIndex: 2, width: '100%' }}>
              <button onClick={handleSaveAiSettings} disabled={isSaving} style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center', background: THEME.accentGradient, color: '#0f172a', border: 'none', padding: '18px 30px', borderRadius: '16px', fontWeight: '900', fontSize: '15px', cursor: 'pointer', boxShadow: '0 10px 25px -5px rgba(45, 212, 191, 0.5)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '10px' }}>
                {isSaving ? 'Encrypting & Saving...' : <><FaMicrochip size={18}/> Deploy Intelligence</>}
              </button>
            </div>
            
            {/* Decorative Background Elements */}
            <FaNetworkWired style={{ position: 'absolute', right: isMobile ? '-20px' : '-50px', bottom: isMobile ? '-20px' : '-50px', fontSize: isMobile ? '200px' : '350px', color: 'white', opacity: 0.03, zIndex: 1, animation: 'float 12s infinite ease-in-out' }}/>
            <div style={{ position: 'absolute', top: '-100px', left: '-100px', width: '300px', height: '300px', background: THEME.accent, opacity: 0.15, filter: 'blur(100px)', borderRadius: '50%', zIndex: 1 }}></div>
          </div>

          <div className="animate-up delay-1" style={{ marginTop: '25px', padding: isMobile ? '20px' : '25px 30px', background: 'white', borderRadius: '24px', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', alignItems: isMobile ? 'center' : 'flex-start', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', textAlign: isMobile ? 'center' : 'left' }}>
            <div style={{background: '#eff6ff', padding: '12px', borderRadius: '14px', color: '#3b82f6', flexShrink: 0}}><FaShieldAlt size={24} /></div>
            <div style={{ fontSize: '13px', color: THEME.textMain, lineHeight: '1.6', fontWeight: '500' }}>
              <strong style={{color: THEME.primary, fontWeight: '900', display: 'block', marginBottom: '5px', fontSize: '14px'}}>SaaS Architecture Note:</strong> 
              When the AI Engine is turned on, your specific business identity (the prompt above) is securely routed to our centralized Google Gemini servers. Keyword rules will be suspended while the AI is active, allowing natural conversations.
            </div>
          </div>
        </div>
      )}

      {/* =========================================
          ULTRA MODAL: CREATE/EDIT KEYWORD RULE
      ========================================= */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(12px)', zIndex: 100, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? '0px' : '20px' }}>
          <div style={{ background: 'white', width: '100%', maxWidth: '700px', borderRadius: isMobile ? '32px 32px 0 0' : '32px', overflow: 'hidden', boxShadow: '0 40px 80px -20px rgba(0, 0, 0, 0.5)', display: 'flex', flexDirection: 'column', maxHeight: isMobile ? '85vh' : '90vh', border: '1px solid rgba(255,255,255,0.2)' }}>
            
            <div style={{ padding: isMobile ? '20px' : '30px 40px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: isMobile ? '20px' : '24px', fontWeight: '900', color: THEME.textMain, letterSpacing: '-0.5px' }}>{editingRuleId ? 'Edit Logic Flow' : 'Create Logic Flow'}</h3>
                <p style={{ margin: 0, fontSize: '13px', color: THEME.textSub, fontWeight: '500' }}>Define exact triggers and automated responses.</p>
              </div>
              <button onClick={() => setShowModal(false)} style={{ border: 'none', background: 'white', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.textSub, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: 'all 0.2s', flexShrink: 0 }}><FaTimes size={16}/></button>
            </div>

            <form onSubmit={handleSaveRule} style={{ padding: isMobile ? '20px' : '40px', overflowY: 'auto' }}>
              
              <div style={{ marginBottom: '35px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: THEME.primary, marginBottom: '15px', letterSpacing: '0.5px' }}><span style={{background:THEME.primaryLight, padding:'4px 8px', borderRadius:'8px', marginRight:'8px', color: THEME.primary, backgroundColor: '#ede9fe'}}>1</span> IF CUSTOMER SAYS...</label>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '15px' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <select value={matchType} onChange={(e) => setMatchType(e.target.value)} style={{ width: '100%', padding: '16px', borderRadius: '16px', border: `2px solid ${THEME.border}`, outline: 'none', background: THEME.bg, fontSize: '14px', fontWeight: '700', color: THEME.textMain, appearance: 'none', boxSizing: 'border-box', cursor: 'pointer' }}>
                      <option value="contains">Text Contains</option>
                      <option value="exact">Exact Match Only</option>
                    </select>
                    <div style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#cbd5e1' }}>▼</div>
                  </div>
                  <div style={{ flex: 2 }}>
                    <input required placeholder="e.g. menu, price, location" value={triggerKeyword} onChange={(e) => setTriggerKeyword(e.target.value)} style={{ width: '100%', padding: '16px', borderRadius: '16px', border: `2px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box', background: THEME.bg, transition: 'border 0.2s' }} />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: THEME.success, marginBottom: '15px', letterSpacing: '0.5px' }}><span style={{background:'#dcfce7', padding:'4px 8px', borderRadius:'8px', marginRight:'8px'}}>2</span> THEN SYSTEM EXECUTES...</label>
                
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px', marginBottom: '20px' }}>
                  <div onClick={() => {setActionType('text'); setSelectedTemplate("");}} style={{ flex: 1, padding: '16px', borderRadius: '16px', border: actionType === 'text' ? `2px solid ${THEME.success}` : `2px solid ${THEME.border}`, background: actionType === 'text' ? '#ecfdf5' : 'white', color: actionType === 'text' ? THEME.success : THEME.textSub, fontWeight: '800', fontSize: '13px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s', boxSizing: 'border-box' }}>Raw Text Reply</div>
                  <div onClick={() => {setActionType('template'); setReplyText("");}} style={{ flex: 1, padding: '16px', borderRadius: '16px', border: actionType === 'template' ? `2px solid ${THEME.success}` : `2px solid ${THEME.border}`, background: actionType === 'template' ? '#ecfdf5' : 'white', color: actionType === 'template' ? THEME.success : THEME.textSub, fontWeight: '800', fontSize: '13px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s', boxSizing: 'border-box' }}>Meta Template</div>
                </div>

                {actionType === 'text' ? (
                  <textarea required placeholder="Type the exact response you want the bot to send..." value={replyText} onChange={(e) => setReplyText(e.target.value)} rows="5" className="custom-scroll" style={{ width: '100%', padding: '20px', borderRadius: '16px', border: `2px solid ${THEME.border}`, outline: 'none', fontSize: '14px', fontWeight: '500', resize: 'vertical', boxSizing: 'border-box', background: THEME.bg, lineHeight: '1.6' }} />
                ) : (
                  <div style={{ position: 'relative' }}>
                      <select required value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ width: '100%', padding: '16px 20px', borderRadius: '16px', border: `2px solid ${THEME.border}`, outline: 'none', background: THEME.bg, fontSize: '14px', fontWeight: '700', boxSizing: 'border-box', appearance: 'none', cursor: 'pointer' }}>
                        <option value="" disabled>-- Select an Approved Template --</option>
                        {templates.filter(t => t.status === "Approved").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <div style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#cbd5e1' }}>▼</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '16px', borderRadius: '16px', border: 'none', background: '#f1f5f9', color: '#64748b', fontWeight: '800', fontSize: '14px', cursor: 'pointer', width: '100%' }}>Cancel</button>
                <button type="submit" disabled={isSaving} style={{ flex: 2, padding: '16px', borderRadius: '16px', border: 'none', background: THEME.primaryGradient, color: 'white', fontWeight: '900', fontSize: '15px', cursor: isSaving ? 'wait' : 'pointer', boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.4)', transition: 'transform 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%' }}>
                  {isSaving ? 'Encrypting...' : (editingRuleId ? <><FaPen/> Update Logic</> : <><FaBolt/> Deploy Rule</>)}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BotManager;